import { useDictionary } from '@/core/providers/dictionary-provider';

export interface UseErrorMessageOptions {
  /**
   * Optional overrides for specific error codes.
   * These keys override the global errorCodes map.
   */
  overrideMap?: Record<string, string | undefined>;

  /**
   * Optional custom fallback message.
   * If not provided, a generic localized fallback is used.
   */
  fallback?: string;
}

/**
 * Global error-code → localized-message mapper.
 *
 * - Uses `dict.errorCodes` as the single source of truth for error messages.
 * - Callers can optionally override or extend specific codes via `overrideMap`.
 */
export function useErrorMessage() {
  const dict = useDictionary();
  const errorCodes = dict.errorCodes as
    | Record<string, string | undefined>
    | undefined;

  const defaultFallback =
    errorCodes?.unknownError ?? 'An unknown error occurred.';

  return (
    code: string | undefined,
    options?: UseErrorMessageOptions,
  ): string => {
    const mergedMap: Record<string, string | undefined> = {
      ...(errorCodes ?? {}),
      ...(options?.overrideMap ?? {}),
    };

    if (code && mergedMap[code]) {
      return mergedMap[code] as string;
    }

    return options?.fallback ?? defaultFallback;
  };
}
